import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Badge } from '../components/ui.js';
import { formatMoney, type Currency } from '@chatrunner/schemas';

interface Order {
  order_id: string;
  customer_name: string;
  customer_phone: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'dispatched' | 'delivered' | 'cancelled' | 'returned';
  payment_method: string;
  total_cents: number;
  currency: string;
  created_at: string;
  items: Array<{ title: string; quantity: number }>;
}

const STATUSES = ['pending', 'confirmed', 'preparing', 'dispatched', 'delivered', 'cancelled', 'returned'] as const;
const STATUS_KIND: Record<Order['status'], 'ok' | 'warn' | 'accent' | 'danger' | 'muted'> = {
  pending: 'warn', confirmed: 'accent', preparing: 'warn', dispatched: 'accent',
  delivered: 'ok', cancelled: 'danger', returned: 'muted',
};

export function Orders() {
  const api = useApi();
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const [tab, setTab] = useState<'all' | 'active' | 'done'>('all');
  const { data } = useQuery({ queryKey: ['orders'], queryFn: () => api.get<{ items: Order[] }>('/orders') });
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/orders/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });

  const all = data?.items ?? [];
  const rows = all.filter((o) =>
    tab === 'all' ? true : tab === 'active' ? !['delivered', 'cancelled', 'returned'].includes(o.status) : o.status === 'delivered',
  );
  const dateLocale = lang === 'ar' ? 'ar-EG' : 'en-US';

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.orders.title')} sub={t('page.orders.sub')} />

      <div className="tabs">
        <button className={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>{t('page.orders.tab.all')}<span className="c">{all.length}</span></button>
        <button className={tab === 'active' ? 'on' : ''} onClick={() => setTab('active')}>{t('page.orders.tab.active')}</button>
        <button className={tab === 'done' ? 'on' : ''} onClick={() => setTab('done')}>{t('page.orders.tab.done')}</button>
      </div>

      <div className="card tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('page.orders.col.id')}</th>
              <th>{t('page.orders.col.customer')}</th>
              <th>{t('page.orders.col.items')}</th>
              <th>{t('page.orders.col.total')}</th>
              <th>{t('page.orders.col.status')}</th>
              <th>{t('page.orders.col.date')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.order_id}>
                <td className="mono tbl__primary" dir="ltr" style={{ textAlign: 'start' }}>#{o.order_id.slice(-8)}</td>
                <td>
                  <div className="usercell__name">{o.customer_name}</div>
                  <div className="usercell__meta mono" dir="ltr">{o.customer_phone}</div>
                </td>
                <td className="muted" style={{ fontSize: 12.5 }}>{o.items.map((i) => `${i.quantity}× ${i.title}`).join(', ')}</td>
                <td className="mono tbl__cell-strong">{formatMoney(o.total_cents, o.currency as Currency)}</td>
                <td>
                  <select
                    className="selectbox"
                    style={{ padding: '5px 8px', fontSize: 12.5, width: 'auto' }}
                    value={o.status}
                    onChange={(e) => updateStatus.mutate({ id: o.order_id, status: e.target.value })}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`orderstatus.${s}`)}</option>)}
                  </select>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>{new Date(o.created_at).toLocaleDateString(dateLocale)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 28, color: 'var(--ink-3)' }}>{t('page.orders.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex gap8 wrapf">
        {STATUSES.map((s) => <Badge key={s} kind={STATUS_KIND[s]} plain>{t(`orderstatus.${s}`)}</Badge>)}
      </div>
    </div>
  );
}
